import React, { useEffect, useRef, useState } from "react";
import { BsFillFunnelFill } from "react-icons/bs";
import { IoCaretDown, IoCaretUp, IoClose, IoSearch } from "react-icons/io5";
import SearchBar from "./SearchBar";
import { DateFilterField, DateRangeFilterField, DropdownFilterField } from "./FilterFields";

const ColumnHeaderControls = ({ column, descriptor, filters, initialFilters, onFilterChange }) => {
    const dropdownRef = useRef(null);
    const [draft, setDraft] = useState(filters);

    const searchIsActive = !!(descriptor.searchEntry && filters?.search && filters?.searchKey === descriptor.searchEntry.key);
    const [searchOpen, setSearchOpen] = useState(searchIsActive);
    useEffect(() => {
        if (searchIsActive) setSearchOpen(true);
    }, [searchIsActive]);

    const popoverKeys = [
        ...descriptor.dropdowns.map((group) => group.key),
        ...descriptor.dates.map((picker) => picker.key),
        ...descriptor.dateRanges.flatMap((range) => [range.from.key, range.to.key]),
    ];

    useEffect(() => {
        const el = dropdownRef.current;
        if (!el) return undefined;
        const handleShow = () => setDraft(filters);
        el.addEventListener("show.bs.dropdown", handleShow);
        return () => el.removeEventListener("show.bs.dropdown", handleShow);
    }, [filters]);

    const updateDraft = (key, value) => setDraft((prev) => ({ ...prev, [key]: value }));

    const closePopover = () => {
        const toggle = dropdownRef.current?.querySelector("[data-bs-toggle='dropdown']");
        if (toggle && window.bootstrap) {
            window.bootstrap.Dropdown.getOrCreateInstance(toggle).hide();
        }
    };

    const hasDraftChanges = popoverKeys.some((key) => draft[key] !== filters[key]);
    const activeCount = popoverKeys.filter((key) => filters[key] !== initialFilters?.[key]).length;
    const isColumnFiltered = activeCount > 0;

    const applyPopoverFilters = () => {
        popoverKeys.forEach((key) => {
            if (draft[key] !== filters[key]) onFilterChange(key, draft[key]);
        });
        closePopover();
    };

    const clearPopoverFilters = () => {
        popoverKeys.forEach((key) => {
            if (filters[key] !== initialFilters?.[key]) onFilterChange(key, initialFilters?.[key]);
        });
        closePopover();
    };

    const setSort = (direction) => {
        onFilterChange("sortBy", column.key);
        onFilterChange("sort", direction);
    };
    const isSortedAsc = descriptor.sort && filters.sortBy === column.key && filters.sort === false;
    const isSortedDesc = descriptor.sort && filters.sortBy === column.key && filters.sort === true;

    const closeSearch = () => {
        setSearchOpen(false);
        if (searchIsActive) {
            onFilterChange("search", "");
        }
    };

    if (!descriptor.hasAnyControl) {
        return <span className="list-th-label">{column.name}</span>;
    }

    return (
        <div className={`list-th-inner ${isSortedAsc || isSortedDesc ? "is-sorted" : ""}`}>
            <span className="list-th-label" title={column.name}>{column.name}</span>

            {descriptor.sort ? (
                <span className="list-th-sort" role="group" aria-label={`Sort by ${descriptor.sort.name}`}>
                    <button
                        type="button"
                        className={`list-th-sort-btn ${isSortedAsc ? "active" : ""}`}
                        onClick={() => setSort(false)}
                        title="Sort ascending"
                        aria-label={`Sort ${descriptor.sort.name} ascending`}
                        aria-pressed={isSortedAsc}
                    >
                        <IoCaretUp />
                    </button>
                    <button
                        type="button"
                        className={`list-th-sort-btn ${isSortedDesc ? "active" : ""}`}
                        onClick={() => setSort(true)}
                        title="Sort descending"
                        aria-label={`Sort ${descriptor.sort.name} descending`}
                        aria-pressed={isSortedDesc}
                    >
                        <IoCaretDown />
                    </button>
                </span>
            ) : null}

            {descriptor.searchEntry ? (
                searchOpen ? (
                    <div className="list-th-search" onClick={(e) => e.stopPropagation()}>
                        <SearchBar
                            title={descriptor.searchEntry.value}
                            placeholder={descriptor.searchEntry.value}
                            searchKeys={[descriptor.searchEntry]}
                            filters={filters}
                            onFilterChange={onFilterChange}
                        />
                        <button
                            type="button"
                            className="list-th-search-close"
                            onClick={closeSearch}
                            aria-label="Close search"
                            title="Close search"
                        >
                            <IoClose />
                        </button>
                    </div>
                ) : (
                    <button
                        type="button"
                        className="list-th-icon-btn"
                        onClick={(e) => { e.stopPropagation(); setSearchOpen(true); }}
                        aria-label={`Search by ${descriptor.searchEntry.value}`}
                        title={`Search by ${descriptor.searchEntry.value}`}
                    >
                        <IoSearch />
                    </button>
                )
            ) : null}

            {descriptor.hasPopoverFilters ? (
                <div className="dropdown list-th-filter-dropdown" ref={dropdownRef}>
                    <button
                        type="button"
                        className={`list-th-icon-btn list-th-filter-btn ${isColumnFiltered ? "is-active" : ""}`}
                        data-bs-toggle="dropdown"
                        data-bs-auto-close="outside"
                        aria-label={`Filter ${column.name}`}
                        title={`Filter ${column.name}`}
                    >
                        <BsFillFunnelFill />
                        {isColumnFiltered ? <span className="list-th-filter-badge" aria-hidden="true" /> : null}
                    </button>
                    <div className="dropdown-menu list-th-filter-menu p-3" style={{ position: "relative", zIndex: 1060 }}>
                        <div className="list-th-filter-menu-header">
                            <span className="list-th-filter-menu-title">Filter &middot; {column.name}</span>
                            {activeCount ? <span className="list-th-filter-menu-count">{activeCount} active</span> : null}
                        </div>
                        <div className="d-flex flex-column gap-3">
                            {descriptor.dropdowns.map((group) => (
                                <DropdownFilterField
                                    key={group.key}
                                    group={group}
                                    value={draft[group.key]}
                                    onChange={(value) => updateDraft(group.key, value)}
                                />
                            ))}
                            {descriptor.dates.map((picker) => (
                                <DateFilterField
                                    key={picker.key}
                                    picker={picker}
                                    value={draft[picker.key]}
                                    onChange={(value) => updateDraft(picker.key, value)}
                                />
                            ))}
                            {descriptor.dateRanges.map((range) => (
                                <DateRangeFilterField
                                    key={range.key}
                                    range={range}
                                    fromValue={draft[range.from.key]}
                                    toValue={draft[range.to.key]}
                                    onFromChange={(value) => updateDraft(range.from.key, value)}
                                    onToChange={(value) => updateDraft(range.to.key, value)}
                                />
                            ))}
                        </div>
                        <div className="d-flex justify-content-between align-items-center gap-2 mt-3 pt-3 border-top">
                            <button type="button" className="btn btn-sm resetBtn border-0 fw-bold" onClick={clearPopoverFilters} disabled={!activeCount}>
                                Clear
                            </button>
                            <button type="button" className="btn btn-primary btn-sm px-3" onClick={applyPopoverFilters} disabled={!hasDraftChanges}>
                                Apply
                            </button>
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
};

export default ColumnHeaderControls;
